import { useQuery } from 'react-query'
import { useEnvVars } from './useEnvVars'
import { Coordinates } from '../types/Order'

/**
 * Address the geocoding service already holds for the resolution behind a pin
 * link, in the customer-facing terms the pin-confirm form speaks. The wire ->
 * UI mapping is applied when parsing the response (see `toPinLinkContext`); it
 * is the exact inverse of the UI -> wire mapping in `useConfirmPinLocation`.
 */
export type PinLinkAddress = {
  /** wire `address.state` -> UI "Emirate" */
  emirate?: string
  /** wire `address.area` -> UI "Area" */
  area?: string
  /** wire `address.floor` -> UI "Building name" */
  buildingName?: string
  /** wire `address.flat` -> UI "Apartment / villa number" */
  apartmentNumber?: string
}

/**
 * What the page knows about the link before the customer touches anything:
 * the service's best-guess point (null when it has none) and whatever address
 * components it already holds (each key absent when the service has it empty).
 */
export type PinLinkContext = {
  coordinates: Coordinates | null
  address: PinLinkAddress
}

/**
 * Wire shape of the `address` block, shared by every public link endpoint that
 * returns an address: this one and `address-detail` (story sc-27850 Fix 2).
 * Each field is omitted when the service does not know it, so a street address
 * with no building legitimately arrives as `{ state: 'Ajman' }`.
 */
export type PinLinkAddressResponse = {
  area?: string
  state?: string
  floor?: string
  flat?: string
}

/** Wire shape of a `coordinates` block. Nullable, and every field optional. */
export type CoordinatesResponse = { lat?: number; lng?: number } | null

/** Wire shape of `GET /v1/resolutions/link`. Every field is optional. */
type PinLinkContextResponse = {
  coordinates?: CoordinatesResponse
  address?: PinLinkAddressResponse
}

/**
 * The "we learned nothing" result. Returned for every failure mode — no base
 * URL, no token, transport error, any non-200, unparseable body — so a failed
 * context read is indistinguishable from a link the service holds no data for,
 * and the page falls back to exactly its pre-sc-27850 behaviour.
 */
const EMPTY_CONTEXT: PinLinkContext = { coordinates: null, address: {} }

/** Keeps a wire string only when it carries something after trimming. */
function presentString(value?: string): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * The single wire -> UI mapping for the `address` block: `state` -> Emirate,
 * `area` -> Area, `floor` -> Building name, `flat` -> Apartment, with anything
 * blank after trimming dropped. Exported because `useAddressDetail` parses the
 * same block off its own endpoint — one mapping means the two consumers of it
 * cannot drift apart. It is the exact inverse of `useConfirmPinLocation`'s.
 */
export function toPinLinkAddress(
  address?: PinLinkAddressResponse
): PinLinkAddress {
  return {
    emirate: presentString(address?.state),
    area: presentString(address?.area),
    buildingName: presentString(address?.floor),
    apartmentNumber: presentString(address?.flat),
  }
}

/**
 * The single "is there actually a point here?" guard for every public link
 * endpoint that returns coordinates (this one and `address-detail`).
 *
 * Returns `null` for anything that is not two finite numbers. That covers the
 * service's explicit `coordinates: null` — a documented, normal answer meaning
 * "no point", which `address-detail` returns for a retired place id — as well as
 * a half-populated or non-numeric block. All of them must stay `null`: defaulting
 * a missing lat/lng to `0` would put the pin at 0,0, a real point in the Gulf of
 * Guinea, and passing `NaN` through would leave the map somewhere undefined.
 *
 * Exported so the two consumers share one guard and cannot drift, the same
 * reason `toPinLinkAddress` is shared.
 */
export function toPresentCoordinates(
  block?: CoordinatesResponse
): Coordinates | null {
  const lat = block?.lat
  const lng = block?.lng

  if (
    typeof lat !== 'number' ||
    !Number.isFinite(lat) ||
    typeof lng !== 'number' ||
    !Number.isFinite(lng)
  ) {
    return null
  }

  return { lat, lng }
}

/** Parses the wire body into UI terms, defensively — see the two helpers above. */
function toPinLinkContext(body: PinLinkContextResponse): PinLinkContext {
  return {
    coordinates: toPresentCoordinates(body.coordinates),
    address: toPinLinkAddress(body.address),
  }
}

/**
 * Reads the public, token-gated link context for the pin-confirm page, so the
 * pin can start on the order's best-guess point and the address form can open
 * pre-filled instead of blank (story sc-27850).
 *
 * Contract (geocoding service, sc-27850 backend):
 *   GET {GEOCODING_URL}/v1/resolutions/link?token={token}
 *   -> 200 { coordinates: { lat, lng } | null,
 *            address?: { area, state, floor, flat } }   (fields omitted when empty)
 *   -> 400 "invalid link" / "link expired", 404 not found — exactly as
 *      confirm-link.
 *
 * **This hook deliberately never surfaces an error.** `confirm-link` stays the
 * sole authority on whether a link is alive: it is the call that actually has
 * to succeed, and it already drives the "link expired" screen. A failed context
 * read only costs the customer the head start (pre-filled form, pre-positioned
 * pin) — they can still drag the pin and type the address by hand — so every
 * failure resolves to `EMPTY_CONTEXT` and the page silently degrades to its
 * previous behaviour. Blocking a customer who could still pin manually because
 * an optional read failed would be strictly worse. `retry: false` for the same
 * reason: there is nothing worth waiting on.
 *
 * The `token` is the opaque signed string from the URL path, carried through
 * verbatim and never decoded client-side; it is the only credential, so no
 * Authorization header is sent.
 *
 * Raw `fetch` with NO request headers, for the same reasons as
 * `useCitiesForEmirate`: a bare cross-origin GET carrying only CORS-safelisted
 * headers is a "simple request", so the browser skips the OPTIONS preflight.
 * Adding `Content-Type` (pointless on a bodyless GET) would force a preflight
 * and widen what that public endpoint's CORS config has to allow.
 */
export function usePinLinkContext(token?: string) {
  const { env } = useEnvVars()
  const baseUrl = env.GEOCODING_URL
  const enabled = !!baseUrl && !!token

  const { data } = useQuery<PinLinkContext>(
    ['pin-link-context', token],
    async () => {
      const url = `${baseUrl}/v1/resolutions/link?token=${encodeURIComponent(
        token as string
      )}`

      let response: Response
      try {
        // No headers: keep it a simple (preflight-free) cross-origin GET.
        response = await fetch(url)
      } catch {
        // Transport failure — degrade to today's behaviour.
        return EMPTY_CONTEXT
      }

      if (!response.ok) {
        // 400 (invalid/expired) or 404 (gone) or anything else: no head start.
        // NOT escalated to the expired screen — see the doc comment above.
        return EMPTY_CONTEXT
      }

      try {
        const body = (await response.json()) as PinLinkContextResponse
        return toPinLinkContext(body ?? {})
      } catch {
        return EMPTY_CONTEXT
      }
    },
    {
      enabled,
      // The resolution behind a link doesn't change while the customer is on
      // the page, and this only seeds initial state — never refetch.
      staleTime: Infinity,
      retry: false,
    }
  )

  // `context` only — deliberately no loading flag. The map must always render:
  // exposing one invites a consumer to gate on it and reintroduce the stall hole
  // where a slow or stalled optional read leaves the map area blank. Until the
  // read lands (or if it never does) the context is simply `EMPTY_CONTEXT`, so
  // the page opens on its honest fallback and moves once it learns better.
  return { context: data ?? EMPTY_CONTEXT }
}
